import { useEffect, useId, useMemo, useState } from "react";
import { useField } from "formik";
import { Combobox } from "@base-ui/react/combobox";
import {
  IconChevronDown,
  IconSquareCheckFilled,
  IconX,
} from "@tabler/icons-react";
import styles from "./MultiSelectDropdown.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import useDialogPortalContainer from "../../hooks/use-dialog-portal-container.ts";
import Text from "./Text.tsx";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface CreatableOption extends MultiSelectOption {
  creatable?: boolean;
}

interface MultiSelectDropdownBaseProps {
  options: MultiSelectOption[];
  label: string;
  hiddenLabel?: boolean;
  placeholder?: string;
  allowCustom?: boolean;
  className?: string;
}

interface MultiSelectDropdownProps extends MultiSelectDropdownBaseProps {
  name: string;
}

export default function MultiSelectDropdown({
  name,
  options,
  label,
  hiddenLabel = false,
  placeholder = "Sélectionner",
  allowCustom = false,
  className,
}: MultiSelectDropdownProps) {
  const [field, , helpers] = useField<string[]>(name);

  const value = useMemo<MultiSelectOption[]>(
    () =>
      (field.value || []).map(
        (v) => options.find((o) => o.value === v) ?? { value: v, label: v },
      ),
    [field.value, options],
  );

  return (
    <MultiSelectDropdownStandalone
      value={value}
      onChange={(opts) => helpers.setValue(opts.map((o) => o.value))}
      options={options}
      label={label}
      hiddenLabel={hiddenLabel}
      placeholder={placeholder}
      allowCustom={allowCustom}
      className={className}
    />
  );
}

interface MultiSelectDropdownStandaloneProps
  extends MultiSelectDropdownBaseProps {
  value: MultiSelectOption[];
  onChange: (value: MultiSelectOption[]) => void;
}

export function MultiSelectDropdownStandalone({
  value,
  onChange,
  options,
  label,
  hiddenLabel = false,
  placeholder = "Sélectionner",
  allowCustom = false,
  className,
}: MultiSelectDropdownStandaloneProps) {
  const { container, triggerRef } = useDialogPortalContainer();
  const [inputValue, setInputValue] = useState("");
  const [seenOptions, setSeenOptions] = useState<MultiSelectOption[]>(
    () => value,
  );
  const inputId = useId();

  useEffect(() => {
    setSeenOptions((prev) => {
      const map = new Map(prev.map((o) => [o.value, o]));
      let changed = false;
      for (const opt of value) {
        if (!map.has(opt.value)) {
          map.set(opt.value, opt);
          changed = true;
        }
      }
      return changed ? Array.from(map.values()) : prev;
    });
  }, [value]);

  const allOptions = useMemo<MultiSelectOption[]>(() => {
    const map = new Map<string, MultiSelectOption>();
    for (const opt of [...options, ...seenOptions]) {
      if (!map.has(opt.value)) map.set(opt.value, opt);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [options, seenOptions]);

  const renderItems = useMemo<CreatableOption[]>(() => {
    const query = inputValue.trim().toLowerCase();
    const filtered = query
      ? allOptions.filter((opt) => opt.label.toLowerCase().includes(query))
      : allOptions;

    const trimmed = inputValue.trim();
    const showCreate =
      allowCustom &&
      trimmed.length > 0 &&
      !allOptions.some(
        (opt) => opt.label.toLowerCase() === trimmed.toLowerCase(),
      );

    return showCreate
      ? [...filtered, { creatable: true, value: trimmed, label: trimmed }]
      : filtered;
  }, [allOptions, inputValue, allowCustom]);

  function handleValueChange(rawValue: CreatableOption[]) {
    const seen = new Set<string>();
    const finalOptions: MultiSelectOption[] = [];
    for (const opt of rawValue) {
      if (seen.has(opt.value)) continue;
      seen.add(opt.value);
      finalOptions.push({ value: opt.value, label: opt.label });
    }

    setSeenOptions((prev) => {
      const map = new Map(prev.map((o) => [o.value, o]));
      for (const opt of finalOptions) {
        if (!map.has(opt.value)) map.set(opt.value, opt);
      }
      return Array.from(map.values());
    });

    onChange(finalOptions);
    setInputValue("");
  }

  return (
    <div className={classnames(styles.container, className)}>
      {label && !hiddenLabel && (
        <Text as="div" className={styles.label}>
          <label htmlFor={inputId}>{label}</label>
        </Text>
      )}
      <Combobox.Root<CreatableOption, true>
        items={renderItems}
        multiple
        value={value}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        isItemEqualToValue={(a, b) => a.value === b.value}
        autoHighlight
      >
        <Combobox.InputGroup ref={triggerRef} className={styles.trigger}>
          <Combobox.Chips className={styles.chips}>
            <Combobox.Value>
              {(selected: MultiSelectOption[]) =>
                selected.map((opt) => (
                  <Combobox.Chip key={opt.value}>
                    <Text
                      as="span"
                      variant="deci"
                      color="info"
                      background
                      className={styles.chip}
                    >
                      {opt.label}
                      <Combobox.ChipRemove
                        className={styles.chipRemove}
                        aria-label={`Retirer ${opt.label}`}
                      >
                        <IconX size={13} />
                      </Combobox.ChipRemove>
                    </Text>
                  </Combobox.Chip>
                ))
              }
            </Combobox.Value>
            <Combobox.Input
              placeholder={value.length === 0 ? placeholder : ""}
              className={styles.input}
              id={inputId}
              aria-label={hiddenLabel ? label : undefined}
            />
          </Combobox.Chips>
          <Combobox.Trigger className={styles.triggerButton}>
            <IconChevronDown size={16} />
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Portal container={container}>
          <Combobox.Positioner sideOffset={4} className={styles.positioner}>
            <Combobox.Popup className={styles.popup}>
              <Combobox.Empty>
                <Text className={styles.empty}>Aucun résultat</Text>
              </Combobox.Empty>
              <Combobox.List>
                {renderItems.map((option) => (
                  <Combobox.Item
                    key={option.creatable ? "__create__" : option.value}
                    value={option}
                    className={classnames(
                      styles.option,
                      option.creatable && styles.createOption,
                    )}
                    render={
                      option.creatable ? (
                        <Text as="span" weight="medium">
                          Créer « {option.label} »
                        </Text>
                      ) : (
                        <div>
                          <Combobox.ItemIndicator
                            className={styles.checkboxIndicator}
                            keepMounted
                          >
                            <IconSquareCheckFilled size={20} />
                          </Combobox.ItemIndicator>
                          <Text>{option.label}</Text>
                        </div>
                      )
                    }
                  />
                ))}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </div>
  );
}
